/**
 * Generic hook that auto-marks stale pending generations as failed
 * and refunds credits via the existing idempotent RPCs.
 *
 * Works for both images and videos — the caller provides the pending items
 * and the Supabase table name. Each item is processed at most once (tracked
 * via a ref) to avoid redundant DB updates.
 */

import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useCredits } from "@/contexts/CreditsContext";
import { STALE_GENERATION_TIMEOUT_MS } from "@/config/constants";

interface StaleItem {
  id: string;
  created_at: string;
}

interface UseStaleGenerationCleanupOptions {
  pendingItems: StaleItem[];
  table: "generated_images" | "generated_videos";
  /** Called after stale items are marked as failed (useful for local state cleanup) */
  onCleaned?: (ids: string[]) => void;
}

export function useStaleGenerationCleanup({
  pendingItems,
  table,
  onCleaned,
}: UseStaleGenerationCleanupOptions) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { refreshCredits } = useCredits();
  const cleanedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (pendingItems.length === 0 || !user) return;

    const now = Date.now();
    const staleIds = pendingItems
      .filter((item) => {
        if (cleanedIdsRef.current.has(item.id)) return false;
        const age = now - new Date(item.created_at).getTime();
        return age > STALE_GENERATION_TIMEOUT_MS;
      })
      .map((item) => item.id);

    if (staleIds.length === 0) return;

    // Mark as processed immediately to prevent duplicate runs
    for (const id of staleIds) {
      cleanedIdsRef.current.add(id);
    }

    const markAsFailedAndRefund = async () => {
      const { error } = await supabase
        .from(table)
        .update({ status: "failed" })
        .in("id", staleIds);

      if (error) {
        console.error(`Failed to mark stale ${table} as failed:`, error);
        // Remove from cleaned set so we can retry
        for (const id of staleIds) {
          cleanedIdsRef.current.delete(id);
        }
        return;
      }

      // Refund credits via idempotent SECURITY DEFINER RPCs
      if (table === "generated_images") {
        await supabase.rpc("refund_image_credits", {
          p_image_ids: staleIds,
          p_user_id: user.id,
        });
      } else {
        // Video refund RPC handles one video at a time
        await Promise.all(
          staleIds.map((id) =>
            supabase.rpc("refund_video_credits", {
              p_video_id: id,
              p_user_id: user.id,
            })
          )
        );
      }

      await refreshCredits();
      onCleaned?.(staleIds);
      toast.info(t.generate.staleGenerationCleaned);
    };

    markAsFailedAndRefund();
  }, [pendingItems, table, onCleaned, t, user, refreshCredits]);
}
